import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NotificationContext = createContext({ unread: 0, refresh: () => {}, push: () => {} });

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const wsRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch (_) { /* not logged in yet */ }
  }, []);

  // Initial fetch + WS connection (only when authenticated)
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    refresh();

    let closed = false;
    function connect() {
      try {
        const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
        const ws = new WebSocket(`${base}/api/notifications/ws`);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m.event === "unread" && typeof m.count === "number") setUnread(m.count);
            if (m.event === "notification") {
              setUnread((c) => c + 1);
              toast.message(m.data.title, { description: m.data.body || "", duration: 4500 });
            }
          } catch (_) { /* ping/pong */ }
        };
        ws.onclose = () => { if (!closed) setTimeout(connect, 3000); };
        ws.onerror = () => { try { ws.close(); } catch (_) {} };
      } catch (_) { /* network */ }
    }
    connect();
    return () => { closed = true; try { wsRef.current?.close(); } catch (_) {} };
  }, [user, refresh]);

  return (
    <NotificationContext.Provider value={{ unread, refresh, setUnread }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
